import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, output, signal } from '@angular/core';
import { Subscription, take } from 'rxjs';
import { BbAiDraftingLoader } from '@/app/pages/create-brief-select/ui/bb-ai-drafting-loader/bb-ai-drafting-loader';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { UgcProductResponseDto } from '@/app/common/general-types/products.dto';
import { MonitorTypeEnum } from '@/app/common/general-types/social-screening.dto';
import { ImageService } from '@/app/services/image/image.service';
import { ProductService } from '@/app/services/product/product.service';
import { MONITORING_AI_DRAFT_PHRASES } from '../../monitoring-brief.constants';
import { CreateMonitoring } from '../../services/create-monitoring';
import { MonitoringBriefAiService } from '../../services/monitoring-brief-ai.service';
import { MonitoringDefine } from '../../sections/monitoring-define/monitoring-define';
import { MonitoringRequirements } from '../../sections/monitoring-requirements/monitoring-requirements';
import { ProductSelectMonitoring } from '../../sections/product-select/product-select';
import { MonitoringWindow } from '../../sections/monitoring-window/monitoring-window';
import { MonitoringCreators } from '../../sections/monitoring-creators/monitoring-creators';

type Stage = 'intake' | 'generating' | 'review';

@Component({
  selector: 'cmb-ai-first',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BbAiDraftingLoader,
    MonitoringDefine,
    MonitoringRequirements,
    ProductSelectMonitoring,
    MonitoringWindow,
    MonitoringCreators,
  ],
  templateUrl: './ai-first-monitoring.html',
  styleUrl: './ai-first-monitoring.scss',
})
export class AiFirstMonitoring implements OnInit, OnDestroy {
  readonly service = inject(CreateMonitoring);
  readonly monitoringAi = inject(MonitoringBriefAiService);
  private readonly productService = inject(ProductService);
  private readonly currentUserService = inject(CurrentUserService);
  public readonly imageService = inject(ImageService);

  readonly launch = output<void>();
  readonly startOver = output<void>();

  readonly stage = signal<Stage>('intake');
  readonly prompt = signal<string>('');
  readonly products = signal<UgcProductResponseDto[]>([]);
  readonly aiBusy = signal<boolean>(false);
  readonly phrases = MONITORING_AI_DRAFT_PHRASES;
  private readonly subscriptionList = new Subscription();

  readonly examples: { name: string; description: string; prompt: string }[] = [
    { name: 'Brief matches', description: 'Posts that follow the campaign.', prompt: 'Watch for new posts that show the product being used the way the brief asks, and flag anything off-brief.' },
    { name: 'Brand safety', description: 'Unsafe posts from hired creators.', prompt: 'Watch every new post from these creators and flag anything that is not brand safe.' },
    { name: 'Competitor mentions', description: 'Rival brands in new posts.', prompt: 'Flag new posts that mention or show a competing brand alongside ours.' },
    { name: 'Claims check', description: 'Unapproved product claims.', prompt: 'Watch for new posts that make product claims we have not approved.' },
  ];

  readonly reviewSections = [
    { id: 'brief', title: 'Brief' },
    { id: 'product', title: 'Product' },
    { id: 'requirements', title: 'Requirements' },
    { id: 'window', title: 'Monitoring window' },
    { id: 'creators', title: 'Creators to monitor' },
  ] as const;

  ngOnInit(): void {
    if (this.service.basicInfoModel().briefTitle && this.service.briefTextHtml()) {
      this.stage.set('review');
    }
    const brandId = this.currentUserService.focussedBrand?.id;
    if (brandId) {
      this.subscriptionList.add(
        this.productService
          .getbusinessProducts(brandId)
          .pipe(take(1))
          .subscribe({ next: (response) => this.products.set((response as UgcProductResponseDto[]) ?? []) }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  selectType(type: MonitorTypeEnum): void {
    this.service.monitorType.set(type);
  }

  async generate(): Promise<void> {
    if (this.aiBusy()) {
      return;
    }
    this.aiBusy.set(true);
    this.stage.set('generating');
    const angle = this.prompt().trim() || 'Draft a clear monitoring brief for this product and brand.';
    await this.monitoringAi.draftMonitoringBrief(angle);
    this.aiBusy.set(false);
    this.stage.set('review');
  }

  onStartOver(): void {
    this.prompt.set('');
    this.stage.set('intake');
    this.startOver.emit();
  }

  async regenerate(): Promise<void> {
    if (this.aiBusy()) {
      return;
    }
    this.aiBusy.set(true);
    try {
      await this.monitoringAi.draftMonitoringBrief(
        this.prompt().trim() || 'Regenerate the monitoring requirements for this brief.',
      );
    } finally {
      this.aiBusy.set(false);
    }
  }
}
